import { Area } from '@/constants'
import type { Province, QueryParam, Region } from '@/types'
import { api } from '@/utils'
import { convertQueryParam } from '@/utils/next-util'
import { useCallback, useEffect, useState } from 'react'

export type AddressVersion = 1 | 2

interface UseAreaOptions {
  currentRegion?: string | null
  initialVersion?: AddressVersion
}

export const useArea = (options: UseAreaOptions = {}) => {
  const { currentRegion, initialVersion } = options

  const [addressVersion, setAddressVersion] = useState<AddressVersion>(
    initialVersion || 2
  )
  const [provinces, setProvinces] = useState<Province[]>([])
  const [provinceAvailable, setProvinceAvailable] = useState<Province[]>([])
  const [districts, setDistricts] = useState<Province[]>([])
  const [wards, setWards] = useState<Province[]>([])

  const [provinceCode, setProvinceCode] = useState<string>('')
  const [districtCode, setDistrictCode] = useState<string>('')
  const [wardCode, setWardCode] = useState<string>('')

  const [provinceOfRegion, setProvinceOfRegion] = useState<Region[] | null>(
    null
  )
  const [areasLoading, setAreasLoading] = useState(false)
  const [areasError, setAreasError] = useState('')

  // Helper to build query with version
  const buildAreaQuery = useCallback(
    (params?: QueryParam, version?: AddressVersion) => {
      return convertQueryParam({
        ...params,
        ...(version === 2 ? { version: 2 } : {}),
      } as unknown as QueryParam)
    },
    []
  )

  // Initialize address version from localStorage
  useEffect(() => {
    if (initialVersion) return // Skip if explicitly provided

    const timer = setTimeout(() => {
      if (typeof window !== 'undefined') {
        try {
          const guestInfo = localStorage.getItem('guestInfo')
          if (guestInfo) {
            const parsed = JSON.parse(guestInfo)
            setAddressVersion(parsed?.useAddressV2 ? 2 : 1)
          }
        } catch {
          // Ignore parse errors
        }
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [initialVersion])

  // Reset dependent state when version changes
  useEffect(() => {
    setDistricts([])
    setWards([])
    setProvinceCode('')
    setDistrictCode('')
    setWardCode('')
  }, [addressVersion])

  // Fetch Sale Region Provinces (provinceOfRegion)
  useEffect(() => {
    setAreasLoading(true)
    api(`api/sale-region/province?version=${addressVersion}`, {
      baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      method: 'get',
    })
      .then(result => {
        if (result instanceof Error) {
          setAreasError(result.message)
          return
        }
        setProvinceOfRegion(result.data)
      })
      .finally(() => {
        setAreasLoading(false)
      })
  }, [addressVersion])

  // Fetch Provinces
  const fetchProvinces = useCallback(
    async (version?: AddressVersion) => {
      const ver = version || addressVersion
      setAreasLoading(true)
      try {
        const result = await api(
          `api/area?${buildAreaQuery({ level: Area.PROVINCE }, ver)}`,
          {
            method: 'get',
            baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
          }
        )

        if (result instanceof Error) return

        // Filter by currentRegion if provided
        const filteredProvinces = currentRegion
          ? result.data.filter(
              (e: { saleRegionCode?: string; parentCode?: string }) => {
                if (ver === 2) {
                  return e.parentCode && currentRegion.startsWith(e.parentCode)
                }
                return e.saleRegionCode && e.saleRegionCode === currentRegion
              }
            )
          : result.data

        setProvinces(filteredProvinces)
        setProvinceAvailable(filteredProvinces)
      } finally {
        setAreasLoading(false)
      }
    },
    [addressVersion, currentRegion, buildAreaQuery]
  )

  // Auto-fetch provinces when version or region changes
  useEffect(() => {
    fetchProvinces()
  }, [addressVersion, currentRegion])

  // Fetch Districts when provinceCode changes (Version 1 only)
  useEffect(() => {
    if (!provinceCode || addressVersion === 2) return

    setAreasLoading(true)
    api(
      `api/area?${buildAreaQuery(
        {
          parentCode: provinceCode,
          level: Area.DISTRICT,
        },
        addressVersion
      )}`,
      {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
      }
    )
      .then(result => {
        if (result instanceof Error) return
        setDistricts(result.data)
      })
      .finally(() => setAreasLoading(false))
  }, [provinceCode, addressVersion, buildAreaQuery])

  // Fetch Wards when districtCode changes (Version 1)
  useEffect(() => {
    if (!districtCode || addressVersion === 2) return

    setAreasLoading(true)
    api(
      `api/area?${buildAreaQuery(
        {
          parentCode: districtCode,
          level: Area.WARD,
        },
        addressVersion
      )}`,
      {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
      }
    )
      .then(result => {
        if (result instanceof Error) return
        setWards(result.data)
      })
      .finally(() => setAreasLoading(false))
  }, [districtCode, addressVersion, buildAreaQuery])

  // Fetch Wards when provinceCode changes (Version 2 - direct province to ward)
  useEffect(() => {
    if (!provinceCode || addressVersion !== 2) return

    setAreasLoading(true)
    api(
      `api/area?${buildAreaQuery(
        {
          parentCode: provinceCode,
          level: Area.WARD,
        },
        addressVersion
      )}`,
      {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
      }
    )
      .then(result => {
        if (result instanceof Error) {
          console.error('useArea V2 ward fetch failed:', result)
          return
        }
        console.log('useArea V2 ward fetch succeeded:', result.data)
        setWards(result.data)
      })
      .finally(() => setAreasLoading(false))
  }, [addressVersion, provinceCode, buildAreaQuery])

  // Public methods to trigger fetching
  const getProvinces = useCallback(
    (version?: AddressVersion) => {
      if (version && version !== addressVersion) {
        setAddressVersion(version)
      } else {
        fetchProvinces(version)
      }
    },
    [addressVersion, fetchProvinces]
  )

  const getDistricts = useCallback(
    (code: string) => {
      if (!code) return

      // Only fetch manually if code matches but districts are missing
      const isSameCode = code === provinceCode
      const shouldFetch = isSameCode && districts.length === 0

      setProvinceCode(code)

      if (!isSameCode) {
        // Reset dependent state when province changes
        setDistricts([])
        setWards([])
        setDistrictCode('')
        setWardCode('')
      }

      // Force fetch districts if needed
      if (shouldFetch && addressVersion !== 2) {
        setAreasLoading(true)
        api(
          `api/area?${buildAreaQuery(
            {
              parentCode: code,
              level: Area.DISTRICT,
            },
            addressVersion
          )}`,
          {
            method: 'get',
            baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
          }
        )
          .then(result => {
            if (result instanceof Error) return
            setDistricts(result.data)
          })
          .finally(() => setAreasLoading(false))
      }
    },
    [provinceCode, districts.length, addressVersion, buildAreaQuery]
  )

  const getWards = useCallback(
    (code: string) => {
      console.log('useArea getWards called with code:', code, {
        addressVersion,
        provinceCode,
        districtCode,
      })
      if (!code) return

      const isV2 = addressVersion === 2
      const currentCode = isV2 ? provinceCode : districtCode
      const isSameCode = code === currentCode
      const shouldFetch = isSameCode && wards.length === 0

      if (isV2) {
        setProvinceCode(code)
      } else {
        setDistrictCode(code)
      }

      if (!isSameCode) {
        // Reset dependent state
        setWards([])
        setWardCode('')
      }

      // Force fetch wards if needed
      if (shouldFetch) {
        setAreasLoading(true)
        api(
          `api/area?${buildAreaQuery(
            {
              parentCode: code,
              level: Area.WARD,
            },
            addressVersion
          )}`,
          {
            method: 'get',
            baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
          }
        )
          .then(result => {
            if (result instanceof Error) {
              console.error('useArea getWards manual fetch failed:', result)
              return
            }
            console.log('useArea getWards manual fetch succeeded:', result.data)
            setWards(result.data)
          })
          .finally(() => setAreasLoading(false))
      }
    },
    [districtCode, provinceCode, wards.length, addressVersion, buildAreaQuery]
  )

  // Reset all area state
  const resetArea = useCallback(() => {
    setProvinces([])
    setDistricts([])
    setWards([])
    setProvinceCode('')
    setDistrictCode('')
    setWardCode('')
  }, [])

  return {
    // State
    addressVersion,
    setAddressVersion,
    provinces,
    setProvinces,
    provinceAvailable,
    setProvinceAvailable,
    districts,
    setDistricts,
    wards,
    setWards,
    provinceCode,
    setProvinceCode,
    districtCode,
    setDistrictCode,
    wardCode,
    setWardCode,
    provinceOfRegion,
    setProvinceOfRegion,

    // Loading & Error
    areasLoading,
    areasError,

    // Actions
    getProvinces,
    getDistricts,
    getWards,
    resetArea,
  }
}

export default useArea
